import { DATE_FORMAT } from '@/helpers/dates';
import { PayoutTransactionClientData } from '@/types/Payouts';
import { Box, Button, DateInput, FormikTextInput, Modal, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import * as yup from 'yup';

interface PausedTransactionModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: any) => Promise<void>;
  /**
   * Narrowed to the two fields this modal reads, so it serves a transaction at either level. The
   * payee-level client type is wider than the project-only one and would not satisfy it otherwise.
   */
  transaction: Pick<PayoutTransactionClientData, 'transactionId' | 'pauseExpirationDate'>;
  modalId: string;
  modalTitle: string;
  submitButtonText: string;
}

const pausedTransactionSchema = yup.object({
  expirationDate: yup.string(),
  reason: yup.string().required('Reason for pausing is required'),
});

export const PausedTransactionModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  modalId,
  transaction,
  modalTitle,
  submitButtonText = 'Save Changes',
}: PausedTransactionModalProps) => {
  const initialValues = useMemo(() => {
    return {
      expirationDate: transaction?.pauseExpirationDate
        ? DateTime.fromISO(transaction.pauseExpirationDate).toFormat(DATE_FORMAT)
        : '',
      expirationDays: transaction?.pauseExpirationDate
        ? DateTime.fromISO(transaction.pauseExpirationDate).diffNow('days').days.toFixed(0)
        : '',
      reason: '',
    };
  }, [transaction?.pauseExpirationDate]);

  const showErrorToast = (message: string) => {
    toast.error(message, {
      style: {
        maxWidth: '500px',
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
      },
    });
  };

  const onSubmit = async (values: any, { setSubmitting, resetForm }: FormikHelpers<any>) => {
    try {
      if (transaction.transactionId) {
        await handleSubmit(values);
        resetForm();
        handleClose();
      }
    } catch (error: any) {
      showErrorToast(`Failed to paused transaction: ${error.data.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={pausedTransactionSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      {({ values, isSubmitting, resetForm, submitForm, setValues }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) {
                resetForm();
                handleClose();
              }
            }}
          >
            <Modal.Header
              id={modalId}
              title={modalTitle}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                <DateInput
                  id="expirationDate"
                  name="expirationDate"
                  placeholder="MM-DD-YYYY"
                  label="Expiration Date"
                  value={values.expirationDate}
                  isDisabled={isSubmitting}
                  datePickerProps={{
                    onChange: (date) => {
                      const formattedDate = DateTime.fromJSDate(date as Date)
                        .toLocal()
                        .toFormat(DATE_FORMAT);
                      setValues((prevValues) => ({
                        ...prevValues,
                        expirationDate: formattedDate,
                        expirationDays: Math.ceil(DateTime.fromJSDate(date as Date).diffNow('days').days).toFixed(0),
                      }));
                    },
                    minDate: DateTime.now().plus({ days: 1 }).toJSDate(),
                    selected: values.expirationDate
                      ? DateTime.fromFormat(values.expirationDate.toString(), DATE_FORMAT).toJSDate()
                      : undefined,
                  }}
                  textInputProps={{
                    inputProps: {
                      readOnly: true,
                      width: '130px',
                    },
                  }}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                />
                <Box direction="row" justifyContent="space-between" alignItems="center" gap="md">
                  <Box width="xl">Expiration Days</Box>
                  <Field
                    name="expirationDays"
                    id="expirationDays"
                    type="number"
                    component={FormikTextInput}
                    value={values.expirationDays}
                    isDisabled={isSubmitting}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newValue = e.target.value;
                      if (!newValue || isNaN(parseInt(newValue, 10)) || parseInt(newValue, 10) < 1) {
                        setValues((prevValues) => ({
                          ...prevValues,
                          expirationDays: '',
                          expirationDate: '',
                        }));
                        return;
                      }
                      setValues((prevValues) => ({
                        ...prevValues,
                        expirationDays: newValue,
                        expirationDate: DateTime.now()
                          .plus({ days: parseInt(newValue, 10) })
                          .toFormat(DATE_FORMAT),
                      }));
                    }}
                    width="xl"
                  />
                </Box>
                <Field name="reason" id="reason" label="Reason for Pausing" component={FormikTextInput}></Field>
              </Box>
              <Box color="danger">
                <p>
                  Pausing this transaction will prevent it from being processed. You can unpause it later if needed.
                </p>
              </Box>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
